import { createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import Mdx from "#/components/MdxRemote";
import { metadataToHead } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>
      <main id="content" className="mx-auto mt-24 max-w-3xl space-y-10">
        <h1 className="text-center text-5xl">Job Posting Rules</h1>

        <section className="mx-8 space-y-5">
          <Mdx source={content} />
        </section>
      </main>
      <Footer />
    </>
  );
}

const content = `
**Effective Date: January 31, 2024**

To maintain the highest standard of service and to ensure a productive environment for both job seekers and employers in the healthcare sector, we have established the following job posting rules.

## 1. General Guidelines
   - **Authenticity:** All job postings must represent real and current opportunities in the healthcare and medical field. We do not permit postings for business opportunities, franchises, multi-level marketing, or any position that is not a direct job opportunity.
   - **Clarity and Accuracy:** Job titles and descriptions should be precise and clearly reflect the role, key responsibilities, required certifications, and specific healthcare skills needed.
   - **Non-Discrimination:** Adhere to healthcare industry standards and laws against discrimination. Avoid language that discriminates based on race, gender, religion, national origin, age, disability, or any other protected status.

## 2. Job Details Requirements
   - **Clear Job Titles:** Use standard industry titles to avoid confusion and ensure that professionals can easily find relevant opportunities.
   - **Detailed Descriptions:** Include detailed information about the healthcare facility, department specifics, patient care responsibilities, shift timings, and any necessary qualifications or experience.
   - **Salary Information:** We encourage transparency with salary ranges, benefits, and other compensation details to attract the right candidates.

## 3. Prohibited Content
   - **No Illegal Activities:** Job postings must strictly adhere to legal and ethical medical practices.
   - **No Misleading Information:** Avoid overpromising on job roles, career prospects, or compensation packages.
   - **No Offensive Content:** Job postings should maintain a professional tone and avoid any inappropriate language.

## 4. Application and Hiring Process
   - **Clear Application Instructions:** Include specific steps for application, necessary documentation, and contact details for queries.
   - **Respect Privacy:** Assure candidates of the confidentiality of their personal and professional information.
   - **Equal Opportunity:** Commit to fair and unbiased hiring practices, essential in the healthcare sector.

## 5. Employer Responsibilities
   - **Company Verification:** Employer accounts must be verified, especially to confirm the legitimacy of healthcare facilities.
   - **Regular Updates:** Keep postings current and promptly remove filled positions.
   - **Response Policy:** Timely responses to applicants are crucial in the fast-paced healthcare environment.

## 6. User Interaction and Feedback
   - **Reviews and Ratings:** We provide a platform for professionals to share their experiences with healthcare employers.
   - **User Support:** Our dedicated support team is available to address any concerns or issues related to job postings.

## 7. Compliance and Legal Aspects
   - **Adherence to Employment Laws:** All postings must comply with employment laws relevant to the healthcare sector.
   - **Right to Modify or Remove:** We reserve the right to edit or remove postings that do not adhere to our guidelines.

## 8. Monitoring and Enforcement
   - **Active Monitoring:** We regularly review job postings to ensure they meet our standards.
   - **Penalties for Violations:** Non-compliant postings may be removed, and repeat offenders may face account suspension.

## 9. Feedback and Continuous Improvement
   - **User Feedback Mechanism:** We welcome feedback to continuously improve our platform.
   - **Regular Updates to Rules:** Our policies are regularly updated to reflect changes in the healthcare sector and feedback from our community.

## 10. Accessibility and Inclusivity
   - **Accessible Formats:** We ensure that our job postings are accessible to all healthcare professionals.
   - **Inclusive Language:** We use language that is respectful and inclusive of the diverse healthcare community.

By adhering to these rules, employers can effectively connect with top healthcare professionals, and job seekers can find opportunities that match their skills and aspirations. Together, let's build a thriving and professional healthcare community on Roamdoc.

`;

export const metadata = {
  title: "Job Posting Rules  • Roamdoc",
  description: "Job Posting Rules for Roamdoc.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/job-posting-rules`,
  },
};

export const Route = createFileRoute("/(legal)/job-posting-rules/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
